import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useGoogleLogin } from "@react-oauth/google";
import { Form, Input } from "antd";
import { Button, notification } from "components/common";
import { GoogleIcon } from "components/svg";
import { LOGIN_STORAGE_KEY, USER_STORAGE_KEY } from "constants";
import { useAppMode } from "core/AppModeContext";
import { getDashboardRedirectPath } from "helpers/getDashboardRedirectPath";
import { getDefaultLanguageKey } from "helpers/getDefaultLanguage";
import { getDeveloperDashboardPath } from "helpers/getDeveloperDashboardPath";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  logout,
  setAppToken,
  setAppUser,
  setIsLoggedIn,
} from "store/appSlice";
import { getLoginErrorMessage, updateAuthCredentials } from "utils/authHelpers";
import { setStorageItem } from "utils/localStorage";
import { useMasterOtpMutation, useMasterSignInMutation } from "../api";
import styles from "./impersonate.module.less";

/**
 * Master login (support impersonation): an allow-listed internal employee (the
 * actor) signs in as a developer-portal user (the target) without knowing the
 * target's PIN. Reached from the hidden login-page trigger, which prompts for
 * the target's email and navigates here (/:uuid/impersonate) with it in
 * location state; the uuid is cosmetic — authorization lives entirely in the
 * backend (Google token + allowlist + emailed OTP).
 *
 * Step A shows only the Google button; on success the OTP is requested for the
 * carried target email and the card swaps to step B (OTP + Login). There is no
 * back button or resend — restart from the login page.
 */
const ImpersonateLogin = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { portalMode } = useAppMode();

  const targetEmail = location.state?.targetEmail || "";

  /** false = step A (Google button); true = step B (OTP field + Login). */
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [actorEmail, setActorEmail] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const [masterOtp, { isLoading: otpLoading }] = useMasterOtpMutation();
  const [masterSignIn, { isLoading: signInLoading }] =
    useMasterSignInMutation();

  const loading = isGoogleLoading || otpLoading || signInLoading;

  // Mount clean: purge any existing session so it never leaks into (or gets
  // mixed with) the impersonated one.
  useEffect(() => {
    dispatch(logout());
  }, [dispatch]);

  // No target email means the page was opened directly - restart from the
  // login page, whose hidden trigger prompts for it.
  useEffect(() => {
    if (!targetEmail) {
      navigate(`/${getDefaultLanguageKey() || "en"}/admin/login`, {
        replace: true,
      });
    }
  }, [targetEmail, navigate]);

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await masterOtp({
          access_token: tokenResponse.access_token,
          email: targetEmail,
        }).unwrap();
        setActorEmail(res?.email || "");
        setShowOtpForm(true);
      } catch (err) {
        notification.error({ message: getLoginErrorMessage(err) });
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google OAuth error:", error);
      notification.error({ message: t("Google login failed. Please try again.") });
      setIsGoogleLoading(false);
    },
    onNonOAuthError: () => {
      setIsGoogleLoading(false);
    },
  });

  const onGoogleClick = () => {
    setIsGoogleLoading(true);
    loginWithGoogle();
  };

  const handleAuthSuccess = (response) => {
    const authHeaders = response?.authHeaders;
    const user = response?.user;
    if (user && authHeaders?.["access-token"]) {
      updateAuthCredentials({ data: { user } }, authHeaders);
      dispatch(setAppToken({ token: authHeaders["access-token"] }));
      dispatch(setAppUser({ info: user }));
      setStorageItem(USER_STORAGE_KEY, user);
      setStorageItem(LOGIN_STORAGE_KEY, "yes");
      dispatch(setIsLoggedIn(true));
      navigate(
        portalMode === "developer"
          ? getDeveloperDashboardPath()
          : getDashboardRedirectPath(user),
      );
    } else {
      notification.error({ message: t("Login failed. Please try again.") });
    }
  };

  const onSubmit = async (values) => {
    if (!showOtpForm || loading) return;
    try {
      const res = await masterSignIn({
        actor_email: actorEmail,
        email: targetEmail,
        otp: values.password.trim(),
      }).unwrap();
      handleAuthSuccess(res);
    } catch (err) {
      notification.error({ message: getLoginErrorMessage(err) });
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.card}>
        <h4 className={styles.title}>{t("Impersonate Login")}</h4>

        {!showOtpForm ? (
          <button
            type="button"
            onClick={onGoogleClick}
            disabled={loading}
            className={styles.googleButton}
          >
            {isGoogleLoading || otpLoading ? (
              <LoadingOutlined />
            ) : (
              <GoogleIcon size={20} />
            )}
            <span>{t("Login With Google")}</span>
          </button>
        ) : (
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Form.Item
              name="password"
              label={t("Your OTP")}
              validateTrigger={["onBlur", "onSubmit"]}
              rules={[
                { required: true, message: t("Please type your password") },
              ]}
            >
              <Input.Password
                placeholder={t("Enter OTP")}
                size="large"
                autoComplete="new-password"
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                disabled={loading}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={signInLoading}
              disabled={loading}
              className={styles.loginButton}
            >
              {t("Login")}
            </Button>
          </Form>
        )}
      </div>
    </div>
  );
};

export default ImpersonateLogin;
